import React from 'react';
import * as Yup from 'yup';
import { Box, Button, IconButton, styled, Tooltip, Typography } from '@mui/material';
import { FieldArray, FieldArrayRenderProps, Form, useField, useFormikContext } from 'formik';
import { MuiForm } from 'lib/Form';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import { IOpeningHours } from '@pepperhq/types-sdk';

import { CheckboxFormField } from 'lib/form/fields/CheckboxFormField';
import { TextFormField } from 'lib/form/fields/TextFormField';
import { titleize } from 'lib/helpers';
import { MuiModal } from 'ui/MuiModal';
import {
    DAYS_OF_THE_WEEK,
    DaysOfTheWeek,
    Location,
    RegularOpeningHoursFormValues
} from '../../models/LocationModel';
import { apiRegularToFormValues, formValuesToApiRegular } from '../../openingHoursConverters';
import { getRangeOrderIssues } from '../../locationOpeningHoursHelpers';
import { CopyTargetItem, CopyTimesConfigurationModal } from './CopyTimesConfigurationModal';

interface IProps {
    open: boolean;
    onClose: () => void;
    location: Location;
    onSubmit: (openingHours: IOpeningHours<string>) => Promise<any>;
}

interface FormProps {
    location: Location;
    onSubmit: (openingHours: IOpeningHours<string>) => Promise<any>;
    onClose: () => void;
}

// "00:00" is midnight at the end of the day, i.e. 12am the following day, so a
// slot crosses overnight when to === "00:00" or to < from. Both fields required.
const rangeSchema = Yup.object({
    from: Yup.string().required('Enter an opening time'),
    to: Yup.string().required('Enter a closing time')
});

// Ranges are only validated while the day is open — a closed day discards its
// ranges on submit, so an empty range there should not block the form.
const daySchema = Yup.object({
    closed: Yup.boolean(),
    ranges: Yup.array().when('closed', {
        is: (closed: boolean) => !closed,
        then: schema =>
            schema.of(rangeSchema).test('split-hours-order', function splitHoursOrder(ranges) {
                const list = (ranges ?? []) as { from: string; to: string }[];
                if (list.length < 2) {
                    return true;
                }
                const issues = getRangeOrderIssues(list);
                if (issues.length === 0) {
                    return true;
                }
                return new Yup.ValidationError(
                    issues.map(issue =>
                        this.createError({
                            path: `${this.path}.${issue.index}.${issue.field}`,
                            message: issue.message
                        })
                    )
                );
            })
    })
});

const validationSchema = Yup.object(
    DAYS_OF_THE_WEEK.reduce((acc, day) => {
        acc[day] = daySchema;
        return acc;
    }, {} as Record<DaysOfTheWeek, typeof daySchema>)
);

interface RangeRowProps {
    day: DaysOfTheWeek;
    index: number;
    rangeCount: number;
    arrayHelpers: FieldArrayRenderProps;
}

const RangeRow: React.FC<RangeRowProps> = ({ day, index, rangeCount, arrayHelpers }) => {
    const [fromField, fromMeta] = useField<string>(`${day}.ranges.${index}.from`);
    const [toField, toMeta] = useField<string>(`${day}.ranges.${index}.to`);

    const handleRemove = React.useCallback(() => {
        arrayHelpers.remove(index);
    }, [arrayHelpers, index]);

    const errorMessage =
        (fromMeta.touched && fromMeta.error) || (toMeta.touched && toMeta.error) || undefined;

    const crossesMidnight =
        !!fromField.value &&
        !!toField.value &&
        (toField.value === '00:00' || toField.value < fromField.value);

    return (
        <Box marginBottom={1}>
            <Box display="flex" alignItems="center" gap={1}>
                <TextFormField
                    name={`${day}.ranges.${index}.from`}
                    label="From"
                    type="time"
                    size="small"
                    shrink
                    showHelperText={false}
                    sx={{ flexGrow: 1 }}
                />
                <span>-</span>
                <TextFormField
                    name={`${day}.ranges.${index}.to`}
                    label="To"
                    type="time"
                    size="small"
                    shrink
                    showHelperText={false}
                    sx={{ flexGrow: 1 }}
                />
                <Tooltip title="Delete this time range">
                    <span>
                        <IconButton onClick={handleRemove} disabled={rangeCount <= 1} size="small">
                            <DeleteIcon />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>
            {crossesMidnight && (
                <Typography variant="caption" color="text.disabled" sx={{ whiteSpace: 'nowrap' }}>
                    Time crosses midnight, closing on the next day
                </Typography>
            )}
            {errorMessage && (
                <Typography variant="caption" sx={{ color: 'error.main' }}>
                    {errorMessage}
                </Typography>
            )}
        </Box>
    );
};

interface DayRangesEditorProps {
    day: DaysOfTheWeek;
    arrayHelpers: FieldArrayRenderProps;
}

const DayRangesEditor: React.FC<DayRangesEditorProps> = ({ day, arrayHelpers }) => {
    const { values, setFieldValue } = useFormikContext<RegularOpeningHoursFormValues>();
    const [openCopyModal, setOpenCopyModal] = React.useState(false);
    const ranges = values[day].ranges;

    const copyItems = React.useMemo<CopyTargetItem[]>(
        () =>
            DAYS_OF_THE_WEEK.filter(d => d !== day).map(d => ({
                id: d,
                label: titleize(d),
                closed: values[d].closed
            })),
        [day, values]
    );

    const handleAdd = React.useCallback(() => {
        arrayHelpers.push({ from: '', to: '' });
    }, [arrayHelpers]);

    const handleCopy = React.useCallback(
        (ids: (string | number)[]) => {
            setOpenCopyModal(false);
            ids.forEach(id => {
                const target = id as DaysOfTheWeek;
                setFieldValue(
                    `${target}.ranges`,
                    ranges.map(range => ({ ...range }))
                );
                setFieldValue(`${target}.closed`, false);
            });
        },
        [ranges, setFieldValue]
    );

    const handleOpenCopyModal = React.useCallback(() => {
        setOpenCopyModal(true);
    }, []);

    const handleCloseCopyModal = React.useCallback(() => {
        setOpenCopyModal(false);
    }, []);

    return (
        <Box flex={1}>
            {ranges.map((_range, index) => (
                <RangeRow
                    key={`${day}-range-${index}`}
                    day={day}
                    index={index}
                    rangeCount={ranges.length}
                    arrayHelpers={arrayHelpers}
                />
            ))}
            <Box display="flex" flexDirection="row" gap={1} justifyContent="flex-end">
                <Tooltip title="Add a time range">
                    <span>
                        <IconButton onClick={handleAdd}>
                            <AddIcon />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title="Copy configuration to other days">
                    <span>
                        <IconButton onClick={handleOpenCopyModal}>
                            <ContentCopyIcon />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>
            <CopyTimesConfigurationModal
                open={openCopyModal}
                sourceLabel={titleize(day)}
                items={copyItems}
                onClose={handleCloseCopyModal}
                onSubmit={handleCopy}
            />
        </Box>
    );
};

const StyledDayRow = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2, 0, 1, 0),
    display: 'flex',
    alignItems: 'flex-start',
    gap: 1,
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-of-type': {
        borderBottom: 'none'
    }
}));

interface DayRowProps {
    day: DaysOfTheWeek;
}

const DayRow: React.FC<DayRowProps> = ({ day }) => {
    const [closedField] = useField<boolean>(`${day}.closed`);

    return (
        <StyledDayRow>
            <Box display="flex" flexDirection="column" minWidth={110}>
                <Typography>{titleize(day)}</Typography>
                <CheckboxFormField
                    name={`${day}.closed`}
                    sx={{ fontSize: '14px' }}
                    label={<Typography variant="caption">Closed</Typography>}
                />
            </Box>
            {!closedField.value && (
                <FieldArray name={`${day}.ranges`}>
                    {arrayHelpers => <DayRangesEditor day={day} arrayHelpers={arrayHelpers} />}
                </FieldArray>
            )}
        </StyledDayRow>
    );
};

const OpeningHoursForm: React.FC<FormProps> = ({ location, onClose, onSubmit }) => {
    const initialValues = React.useMemo<RegularOpeningHoursFormValues>(
        () => apiRegularToFormValues(location.openingHours?.regular ?? []),
        [location.openingHours]
    );

    const handleSubmit = React.useCallback(
        (values: RegularOpeningHoursFormValues) =>
            onSubmit({
                regular: formValuesToApiRegular(values),
                overrides: location.openingHours?.overrides ?? []
            }),
        [onSubmit, location.openingHours]
    );

    return (
        <MuiForm<RegularOpeningHoursFormValues>
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
        >
            {({ submitForm, isSubmitting }) => (
                <Form>
                    <Box>
                        {DAYS_OF_THE_WEEK.map(day => (
                            <DayRow key={day} day={day} />
                        ))}
                    </Box>
                    <Box width="100%" marginTop={2} display="flex" justifyContent="flex-end" columnGap={1}>
                        <Button disabled={isSubmitting} variant="outlined" color="primary" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button
                            disabled={isSubmitting}
                            variant="contained"
                            color="primary"
                            onClick={submitForm}
                        >
                            Submit
                        </Button>
                    </Box>
                </Form>
            )}
        </MuiForm>
    );
};

const StyledModalHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderColor: theme.palette.divider,
    borderBottom: `1px solid ${theme.palette.divider}`,
    paddingBottom: theme.spacing(1)
}));

export const LocationRegularOpeningHoursModal: React.FC<IProps> = ({ open, onClose, location, onSubmit }) => (
    <MuiModal
        open={open}
        onClose={onClose}
        paperMaxWidth={600}
        sx={{ '&&': { width: '100%' } }}
        disableBackdropClick
    >
        <>
            <StyledModalHeader>
                <Typography variant="h6">
                    <b>Edit Opening Hours</b>
                </Typography>
                <IconButton onClick={onClose}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </StyledModalHeader>
            <OpeningHoursForm location={location} onSubmit={onSubmit} onClose={onClose} />
        </>
    </MuiModal>
);
